import React from 'react'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  Toggle,
  TooltipUtils,
  MessageBox,
  MESSAGE_BOX_VARIANT_INFO,
  Button,
  BUTTON_VARIANT_SECONDARY,
  SpacingVertical,
  SPACING_8,
  SPACING_16,
  Panel,
  Flex,
  FlexItem,
  Title,
  TITLE_VARIANT_M,
  Grid,
  GridCell,
  GRID_VARIANT_VERTICAL_BIG,
  HelpIcon,
  Text,
} from 'mailup-web-components'

import {
  isValidFormState,
  senderInfoState,
} from 'Pages/EmailSendingWizard/recoil/state/senderInfo'
import InputSenderEmail from './InputSenderEmail'
import InputSenderName from 'Pages/EmailSendingWizard/components/BoxSenderConfig/InputSenderName'
import InputSenderReplyTo from 'Pages/EmailSendingWizard/components/BoxSenderConfig/InputReplyTo'
import DynamicSenderEmail from 'Pages/EmailSendingWizard/components/BoxSenderConfig/DynamicSenderEmail'
import DynamicSenderName from 'Pages/EmailSendingWizard/components/BoxSenderConfig/DynamicSenderName'
import { modalSaveDefaultState } from 'Pages/EmailSendingWizard/recoil/state/modals'
import { evalCondition } from 'mailup-react-helpers'
import BannerMailupShield from 'Components/BannerMailupShield/BannerMailupShield'
import BannerBrandShield from 'Components/BannerMailupShield/BannerBrandShield'

const MessageBoxSenderIsDeleted = () => {
  const intl = useIntl()

  const setSenderInfo = useSetRecoilState(senderInfoState)

  const closeButtonCallback = () => {
    setSenderInfo((prev) => ({
      ...prev,
      senderIsDeleted: false,
    }))
  }

  return (
    <MessageBox
      variant={MESSAGE_BOX_VARIANT_INFO}
      closeButton
      closeButtonCallback={() => closeButtonCallback()}
      data-mu-test="message-box-info"
    >
      <>
        {intl.formatMessage({
          id: 'Load.EmailSendingWizard.DefaultSenderInfo',
        })}
      </>
    </MessageBox>
  )
}

const BoxSenderConfig = () => {
  const intl = useIntl()
  const [
    { isPEC, customSender, senderIsDeleted, savedAsDefault },
    setSenderInfo,
  ] = useRecoilState(senderInfoState)
  const isValidForm = useRecoilValue(isValidFormState)
  const setSaveDefaultModal = useSetRecoilState(modalSaveDefaultState)

  const handleChange = () => {
    setSenderInfo((prev) => ({
      ...prev,
      customSender: !customSender,
    }))
  }

  const handleSave = () => {
    setSaveDefaultModal((prev) => ({
      ...prev,
      show: true,
    }))
  }

  return (
    <>
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.EmailSendingWizard.TitleSender' })}
      </Title>
      <SpacingVertical variant={SPACING_8} />
      <Panel>
        {senderIsDeleted && (
          <>
            <MessageBoxSenderIsDeleted />
            <SpacingVertical variant={SPACING_16} />
          </>
        )}
        <Grid cols={12} variantVertical={GRID_VARIANT_VERTICAL_BIG}>
          <GridCell span={6}>
            <InputSenderEmail />
          </GridCell>
          <GridCell span={6}>
            <InputSenderName />
          </GridCell>
          {!isPEC && (
            <>
              <GridCell span={12}>
                <Flex alignItems="center" height="24px">
                  <Toggle
                    checked={customSender}
                    onChange={handleChange}
                    variant="sm"
                    disabled={
                      evalCondition('uo:EMAILSENDERLOCKED') ||
                      evalCondition('uo:EMAILSENDERLOCKED')
                    }
                    label={intl.formatMessage({
                      id: 'Load.EmailSendingWizard.ChangeSenderName',
                    })}
                  />
                  <FlexItem margin="0 0 0 8px">
                    <Text htmlElement="span">
                      {intl.formatMessage({
                        id: 'Load.EmailSendingWizard.ChangeSenderName',
                      })}
                    </Text>
                  </FlexItem>
                  <TooltipUtils
                    id="customSenderTooltip"
                    type="light"
                    effect="solid"
                    className="c-tooltip"
                  >
                    {intl.formatMessage(
                      {
                        id: 'Load.EmailSendingWizard.DynamicallySelectSenderTooltipIntl',
                      },
                      {
                        linebreak: <br />,
                        strong: (chunk) => <strong>{chunk}</strong>,
                      },
                    )}
                  </TooltipUtils>
                  <span data-tip data-for="customSenderTooltip">
                    <HelpIcon />
                  </span>
                </Flex>
              </GridCell>
              {customSender && (
                <GridCell span={12}>
                  <MessageBox variant="info">
                    {intl.formatMessage({
                      id: 'Load.EmailSendingWizard.DynamicSenderInfo',
                    })}
                  </MessageBox>
                  <SpacingVertical />
                  <Grid cols={12} variantVertical={GRID_VARIANT_VERTICAL_BIG}>
                    <GridCell span={6}>
                      <DynamicSenderEmail />
                    </GridCell>
                    <GridCell span={6}>
                      <DynamicSenderName />
                    </GridCell>
                  </Grid>
                </GridCell>
              )}
            </>
          )}
          <GridCell span={6}>
            <InputSenderReplyTo />
          </GridCell>
          <GridCell span={6}>
            {!savedAsDefault && (
              <>
                <Flex
                  height="100%"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <Button
                    data-mu-test="sending-wizard-save-sender-config"
                    kind={BUTTON_VARIANT_SECONDARY}
                    onClick={handleSave}
                    disabled={!isValidForm}
                  >
                    {intl.formatMessage({
                      id: 'Load.EmailSendingWizard.btnSaveAsDefault',
                    })}
                  </Button>
                </Flex>
              </>
            )}
          </GridCell>
        </Grid>
        <SpacingVertical />
        {/* RIMOSSO TEMPORANEMANENTE <BannerMailupShield /> */}
        <BannerBrandShield />
      </Panel>
    </>
  )
}

export default BoxSenderConfig
